(ns io.daskul.webapp.views.aggregate
  "Generic aggregate views - list, create, edit driven by aggregate config.
   Product-specific behavior injected via configure!."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   ;; Lib modules
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.config.subs :as config.subs]
   [io.daskul.webapp.display-template :as display-template]
   [io.daskul.webapp.panel.events :as panel.events]
   [io.daskul.webapp.panel.subs :as panel.subs]
   [io.daskul.webapp.widgets.embedded :as embedded]
   [io.daskul.webapp.widgets.form.field :as ff]
   [io.daskul.webapp.widgets.child-aggregates :as child-agg]
   [io.daskul.webapp.hooks.aggregates :as hooks]
   [io.daskul.webapp.hooks.list :as list-hooks]
   [io.daskul.webapp.hooks.list-params :as list-params]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.display :as display :refer [display-sections]]
   [io.daskul.webapp.widgets.layout.sidebar :as sidebar]
   [io.daskul.webapp.widgets.record.pinned-facts :as pinned]
   [io.daskul.webapp.widgets.record.alerts :as record.alerts]
   [io.daskul.webapp.widgets.layout.slide-over :as slide-over]
   [io.daskul.webapp.widgets.form.section-nav :as form.section-nav]
   [io.daskul.webapp.widgets.form.section-rows :as section-rows]
   [io.daskul.webapp.widgets.form.sections :as form.sections]
   [io.daskul.webapp.widgets.record.header :as record.header]
   [io.daskul.webapp.views.detail-slots :as detail-slots]
   [io.daskul.webapp.widgets.segmented-filter-bar :as seg-bar]
   [io.daskul.webapp.widgets.form.notes :as notes]
   [io.daskul.webapp.widgets.form.activity :as activity]
   [io.daskul.webapp.widgets.form.auto-fill :as auto-fill]
   [io.daskul.webapp.widgets.form.advanced-fields :as adv-fields]
   [io.daskul.webapp.widgets.form.advanced-section :as advanced-section]))

;; ============================================================================
;; Configuration (product-specific injection)
;; ============================================================================

;; Active context for auto-filtering (F-041).
;; Reads from well-known db path — product stores active-context here.
(rf/reg-sub ::active-context (fn [db _] (:active-context db)))

(defonce ^:private config
  (atom {:navigate-fn nil        ;; (fn [route-name params & [query-params]]) — imperative navigation
         :href-fn nil            ;; (fn [route-name params]) -> url-string
         :current-route-sub nil  ;; re-frame subscription vector, e.g. [::router/current-route]
         :back-fn nil           ;; (fn []) — the record header's Back affordance
         :push-query-params-fn nil    ;; (fn [route-name path-params query-params]) — pushState
         :replace-query-params-fn nil ;; (fn [route-name path-params query-params]) — replaceState
         :custom-edit-details {}            ;; {agg-key component}
         :custom-child-add-components {}    ;; {[parent child] component}
         :custom-child-panels {}            ;; {[parent child] component}
         :custom-create-pages {}            ;; {agg-key component}
         :views {}}))         ;; {agg-key [{:filters :label :label-singular :create}]}

(defn configure!
  "Configure the aggregate views module with product-specific callbacks.

   Options:
   - :navigate-fn — (fn [route-name params & [query-params]]) for imperative
     navigation; query-params is optional (an alert :link's create-page CTA
     passes it — #850)
   - :href-fn — (fn [route-name params]) -> url-string for link generation
   - :current-route-sub — re-frame subscription vector for current route
   - :back-fn — (fn []) invoked by the record header's Back link. Injected
     because Back is history-aware and history lives in the product's router,
     while this namespace is shared.
   - :custom-edit-details — {agg-key component} registry for custom edit cards
   - :custom-child-add-components — {[parent child] component} replaces add form inside slide-over
   - :custom-child-panels — {[parent child] component} replaces entire child tab content
   - :custom-create-pages — {agg-key component} for submit-endpoint aggregates
   - :views — {agg-key [{:filters {:field \"value\"} :label \"Plural\" :label-singular \"Singular\"}]}
              Overrides page title/labels when active filters match a view's filters."
  [opts]
  (swap! config merge opts))

(defn- match-view
  "Match current query params against configured views for an aggregate.
   Returns matching view map ({:label :label-singular}) or nil.
   Query params have string keys; view filters use keyword keys."
  [agg-key query-params]
  (some
   (fn [{:keys [filters] :as view}]
     (when (every? (fn [[k v]]
                     (= (str v) (str (get query-params k))))
                   filters)
       view))
   (get-in @config [:views agg-key])))

(defn- view-filter-keys
  "Get all filter keys used by views for an aggregate. Used to extract
   matching fields from a record for view matching on edit pages."
  [agg-key]
  (->> (get-in @config [:views agg-key])
       (mapcat (comp keys :filters))
       set))

(defn- navigate! [route-name params & [query-params]]
  (when-let [f (:navigate-fn @config)]
    (f route-name params query-params)))

(defn- href [route-name params]
  (when-let [f (:href-fn @config)]
    (f route-name params)))

(defn- current-route-sub []
  (:current-route-sub @config))

(defn- back-fn []
  (:back-fn @config))

(defn- get-custom-edit-details [agg-key]
  (get-in @config [:custom-edit-details agg-key]))

(defn- get-custom-child-add [parent-agg-key child-agg-key]
  (get-in @config [:custom-child-add-components [parent-agg-key child-agg-key]]))

(defn- get-custom-child-panel [parent-agg-key child-agg-key]
  (get-in @config [:custom-child-panels [parent-agg-key child-agg-key]]))

(defn- get-custom-create-page [agg-key]
  (get-in @config [:custom-create-pages agg-key]))

(defn- push-query-params-fn []
  (:push-query-params-fn @config))

(defn- replace-query-params-fn []
  (:replace-query-params-fn @config))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn render-display
  "Render a display vector against a record.
   Display parts: keyword = flat field, vector = nested path, string = literal,
   map = {:get part :fmt token :prefix literal} (#792 — see display-template).
   Returns nil if record is nil or display produces empty string."
  [record display]
  (display-template/render record display))

;; ============================================================================
;; Generic List View
;; ============================================================================

(defn get-field-value
  "Extract field value from an aggregate record.
   Handles both flat and nested :data structures.

   Supports multiple extraction methods via :ui hints:
   - :path - Vector path for nested relationships, e.g., [:user :person :first-name]
   - :relationship - Simple relationship lookup (legacy), e.g., :user looks up (:user record) :name

   Args:
     record - The aggregate record
     field-key - The field key to extract
     field-schema - Optional field schema with :ui hints"
  ([record field-key]
   (get-field-value record field-key nil))
  ([record field-key field-schema]
   (cond
     ;; Path hint - traverse nested relationships
     (get-in field-schema [:ui :path])
     (get-in record (get-in field-schema [:ui :path]))

     ;; Relationship hint - look up :name from related aggregate (legacy)
     (get-in field-schema [:ui :relationship])
     (get-in record [(keyword (get-in field-schema [:ui :relationship])) :name])

     ;; No hints - return raw value
     :else
     (get record field-key))))

(defn- format-date
  "Format ISO date string as short date for display (e.g., 'Feb 18, 2026')."
  [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)]
        (.toLocaleDateString date "en-US" #js {:month "short" :day "numeric" :year "numeric"}))
      (catch :default _
        iso-string))))

(defn- format-datetime
  "Format ISO date string as full date+time for tooltip (e.g., 'Feb 18, 2026 3:13 PM')."
  [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)]
        (.toLocaleString date "en-US" #js {:month "short" :day "numeric" :year "numeric"
                                           :hour "numeric" :minute "2-digit"}))
      (catch :default _
        iso-string))))

(defn- date-column?
  "Check if column has :type :date (handles both keyword and string from JSON)."
  [column]
  (let [t (:type column)]
    (or (= :date t) (= "date" t))))

(defn- get-column-value
  "Extract column value from item using column config.
   Supports :path for both simple ([:a :b]) and compound ([[:a :b] \" \" [:c :d]]) paths."
  [item column]
  (if-let [path (:path column)]
    (display/resolve-path item path)
    (get item (:key column))))

(defn- sort-indicator
  "Render sort icon for a column header.
   Active sort field shows directional chevron, inactive sortable fields show dimmed arrows."
  [field active-field active-dir]
  (if (= field active-field)
    (if (= :asc active-dir)
      ($ icons/icon-chevron-up {:size 14})
      ($ icons/icon-chevron-down {:size 14}))
    ($ icons/icon-arrows-sort {:size 14 :color "var(--mantine-color-dimmed)"})))

;; ============================================================================
;; Panel Form (used in slide-over for create/edit)
;; ============================================================================

(defui panel-form
  "Form component for slide-over panel. Handles both create and edit modes.
   Accepts optional :create-overrides {:inject {field value} :hide [field]}
   to auto-inject values and hide fields when creating from a filtered view."
  [{:keys [agg-key resource config mode entity-id on-close create-overrides]}]
  (let [;; Create overrides: inject values are passed as initial-data, both inject+hide fields are hidden
        inject-data (when (= mode :create) (:inject create-overrides))
        hide-keys (when (= mode :create) (set (:hide create-overrides)))
        excluded-keys (into (set (keys inject-data)) hide-keys)
        ;; Progressive capture (SMS-106): fields in the view's :create {:advanced #{...}}
        ;; render inside a collapsed "More details" disclosure instead of inline.
        ;; Create-only; empty/absent ⇒ flat form (back-compat).
        advanced-keys (when (= mode :create) (set (:advanced create-overrides)))

        ;; Use appropriate hook based on mode
        {:keys [form-data set-field! submit! saving? error schema schema-loading?]}
        (hooks/use-aggregate-form {:aggregate agg-key
                                   :mode mode
                                   :id entity-id
                                   :initial-data inject-data})

        {:keys [delete! deleting? confirm-open? open-confirm! close-confirm!]}
        (hooks/use-aggregate-delete {:aggregate agg-key :id entity-id})

        edit-mode? (= mode :edit)
        delete-enabled? (and edit-mode? (get-in config [:operations :delete]))
        all-fields (:fields schema)
        ;; Filter out injected/hidden fields from the create form
        fields (if (seq excluded-keys)
                 (remove #(contains? excluded-keys (:name %)) all-fields)
                 all-fields)
        loading? (and schema-loading? (nil? form-data))

        ;; Auto-fill: track which fields are currently auto-filled (readonly)
        [auto-filled-keys set-auto-filled-keys!] (uix/use-state #{})
        handle-auto-fill (uix/use-callback
                          (fn [result]
                            (if result
                              (let [{:keys [values readonly-keys]} result]
                                (doseq [[k v] values] (set-field! k v))
                                (set-auto-filled-keys! (or readonly-keys #{})))
                              (do (doseq [k auto-filled-keys] (set-field! k nil))
                                  (set-auto-filled-keys! #{}))))
                          [set-field! auto-filled-keys])]

    ($ :<>
      ($ ui/stack {:gap "md"}
        ;; Error display
        (when-let [form-error (ff/form-level-error-message error fields)]
          ($ ui/error-alert form-error))

        (cond
          loading?
          ($ ui/centered-loader)

          (not schema)
          ($ ui/warning-alert {:title "Loading..."}
            "Fetching form configuration...")

          :else
          (let [;; Split: auto-fill link fields vs normal form fields
                af-fields (filterv #(get-in % [:ui :auto-fill]) fields)
                normal-fields (if (seq af-fields)
                                (remove #(get-in % [:ui :auto-fill]) fields)
                                fields)
                ;; Primary (inline) vs advanced (collapsed) split — SMS-106.
                {:keys [primary advanced]} (adv-fields/split-fields normal-fields advanced-keys)
                ;; Render one normal field — shared by the primary loop and the
                ;; "More details" disclosure so both paths stay identical.
                render-normal
                (fn [field]
                  (let [field-key (:name field)]
                    ($ ff/form-field
                      {:key (name field-key)
                       :agg-key agg-key
                       :field field
                       :value (get form-data field-key)
                       :on-change #(set-field! field-key %)
                       :disabled (contains? auto-filled-keys field-key)
                       :form-data form-data})))
                ;; F-056 create adoption: sections (grouped, row layout) win
                ;; over the flat + "More details" fallback on create.
                create-secs (when (= mode :create)
                              (section-rows/create-sections
                               (get-in config [:form :sections]) normal-fields))
                field-area
                (if (seq create-secs)
                  ($ :<>
                    (for [section create-secs]
                      ($ form.sections/section-fields
                        {:key (name (:id section))
                         :section section
                         :fields normal-fields
                         :agg-key agg-key
                         :form-data form-data
                         :set-field! set-field!
                         :auto-filled-keys auto-filled-keys}))
                    ;; Fields not assigned to any section
                    (for [field (section-rows/unsectioned-fields create-secs normal-fields)]
                      (render-normal field)))
                  ($ :<>
                    ;; Primary normal fields inline
                    (for [field primary]
                      (render-normal field))
                    ;; Advanced normal fields collapsed under "More details"
                    ;; (renders nothing when there are none — see advanced-section)
                    ($ advanced-section/advanced-section
                      {:fields advanced
                       :render-field render-normal})))]
            ($ :form {:onSubmit submit!}
              ($ ui/stack {:gap "md"}
                ;; Auto-fill link widgets at top
                (for [field af-fields]
                  (let [field-key (:name field)]
                    ($ auto-fill/link-widget
                      {:key (name field-key)
                       :field field
                       :value (get form-data field-key)
                       :on-change #(set-field! field-key %)
                       :on-auto-fill handle-auto-fill
                       :disabled (= mode :edit)})))

                field-area

                ;; Action buttons
                ($ ui/group {:justify (if delete-enabled? "space-between" "flex-end")
                             :mt "md" :gap "sm"}
                  ;; Left: Delete button (edit mode only)
                  (when delete-enabled?
                    ($ ui/destructive-button
                      {:variant "subtle"
                       :leftSection ($ icons/icon-trash {:size 16})
                       :onClick open-confirm!}
                      "Delete"))
                  ;; Right: Cancel + Save/Create
                  ($ ui/group {:gap "sm"}
                    ($ ui/button
                      {:variant "subtle" :color "gray"
                       :onClick on-close}
                      "Cancel")
                    ($ ui/button
                      {:type "submit"
                       :loading saving?}
                      (if edit-mode? "Save" "Create")))))))))

      ;; Delete confirmation modal
      (when delete-enabled?
        ($ ui/modal
          {:opened confirm-open?
           :onClose close-confirm!
           :title (str "Delete " (:label-singular config) "?")
           :centered true}
          ($ ui/stack {:gap "md"}
            ($ ui/text
              (str "Are you sure you want to delete this "
                   (str/lower-case (:label-singular config))
                   "? This action cannot be undone."))
            ($ ui/group {:justify "flex-end" :gap "sm"}
              ($ ui/button
                {:variant "subtle" :color "gray"
                 :onClick close-confirm!}
                "Cancel")
              ($ ui/destructive-button
                {:loading deleting?
                 :onClick delete!}
                "Delete"))))))))

;; ============================================================================
;; Generic List View
;; ============================================================================

(defn- build-column-link-fns
  "Build a map of {col-key (fn [item] url-or-nil)} for columns with :link-to.
   Supports :aggregate (navigates to generic edit) or :route (custom named route)."
  [columns agg-key->resource]
  (reduce (fn [acc col]
            (if-let [{:keys [aggregate id-path route]} (:link-to col)]
              (cond
                ;; Custom route — e.g. :conversation/thread
                route
                (assoc acc (:key col)
                       (fn [item]
                         (when-let [id (get-in item id-path)]
                           (href route {:id id}))))
                ;; Standard aggregate edit link
                aggregate
                (if-let [resource (get agg-key->resource aggregate)]
                  (assoc acc (:key col)
                         (fn [item]
                           (when-let [id (get-in item id-path)]
                             (href :aggregate/detail {:agg resource :id id}))))
                  acc)
                :else acc)
              acc))
          {} columns))

(defn- highlight-text
  "Wrap text in a Mantine Highlight component when query is present.

   Splits the query into whitespace-delimited tokens and passes them as a vector
   to Mantine's :highlight prop, which highlights each token independently. This
   correctly highlights matches when the search engine (e.g., tsvector) returned
   a row for word-order-independent or multi-word queries (e.g., 'Reyes Maria'
   should highlight both words in 'Maria Reyes')."
  [text query]
  (if (and query (seq query) (string? text) (not= text "-"))
    (let [tokens (->> (str/split (str/trim query) #"\s+")
                      (remove str/blank?)
                      vec)]
      (if (seq tokens)
        ($ ui/highlight {:highlight (to-array tokens) :size "sm"} text)
        text))
    text))

(defn- render-column-cell
  "Render a single table cell for a list column.
   Extracted to defn to avoid UIx defui macro limitations with for/:let.
   column-link-fns: map of {col-key (fn [item] url-or-nil)} for clickable links.
   visible-from: optional Mantine breakpoint string to hide column on mobile."
  [item col after-read-fields column-link-fns query visible-from]
  (let [col-key (:key col)
        state-ui (get-in item [:_meta :fsm :state])
        link-fn (get column-link-fns col-key)
        link-url (when link-fn (link-fn item))
        content
        (cond
          ;; State column (#858): a dense list defaults to the lighter :indicator
          ;; dot; an explicit column :display overrides the weight. Either way the
          ;; per-row state-UI rides in as :values so colour + label come from the
          ;; one badge source. The focal detail header keeps the full badge.
          (and (= col-key :state) state-ui)
          (display/render-display-value
           (:state item)
           (display/state-cell-display-config (:state item) state-ui (:display col)))

          (get-in after-read-fields [col-key :display])
          (display/render-display-value
           (get-column-value item col)
           (get-in after-read-fields [col-key :display]))

          (:display col)
          (display/render-display-value (get-column-value item col) (:display col))

          (date-column? col)
          (let [raw (get-column-value item col)]
            (if raw
              ($ ui/tooltip {:label (format-datetime raw) :withArrow true}
                ($ ui/text {:size "sm"} (format-date raw)))
              "-"))

          :else
          (let [v (get-column-value item col)]
            (cond
              (nil? v) "-"
              (vector? v) (highlight-text (str/join ", " v) query)
              :else (highlight-text (str v) query))))]
    ($ ui/td {:key (str col-key) :visibleFrom visible-from}
      (if link-url
        (let [text (or (get-in col [:link-to :link-text]) content)]
          ;; :fw "inherit" so a list-cell link tracks its row's body weight
          ;; (#882 lifted body to 500) instead of Mantine Anchor's baked-in 400,
          ;; which otherwise renders the linked column visibly thinner than the
          ;; plain cells beside it. A data-cell link reads like the cell, just
          ;; clickable — hence inherit, matching the :c "inherit" already here.
          ($ ui/anchor {:href link-url :size "sm" :c "inherit" :fw "inherit" :underline "hover"} text))
        content))))

(defn- render-field-cell
  "Render a single table cell for a schema field.
   Extracted to defn to avoid UIx defui macro limitations with for/:let."
  [item field query]
  (let [field-key (:name field)
        state-ui (get-in item [:_meta :fsm :state])]
    ($ ui/td {:key (name field-key)}
      (if (and (= field-key :state) state-ui)
        ;; #858: schema-field list path — still a dense list, so the lighter dot.
        (display/render-display-value
         (:state item) (display/state-cell-display-config (:state item) state-ui nil))
        (let [v (get-field-value item field-key field)
              schema (:schema field)
              widget (get-in field [:ui :widget])
              enum-tag? #(or (= :enum %) (= "enum" %))
              maybe-tag? #(or (= :maybe %) (= "maybe" %))
              enum? (or (and (vector? schema) (enum-tag? (first schema)))
                        (and (vector? schema) (maybe-tag? (first schema))
                             (vector? (second schema))
                             (enum-tag? (first (second schema)))))]
          (cond
            (nil? v) "-"
            (and enum? (string? v)) (highlight-text (display/humanize-enum-value v) query)
            (= widget "textarea") ($ ui/multiline-text {:size "sm"} (str v))
            (vector? v) (highlight-text (str/join ", " v) query)
            :else (highlight-text (str v) query)))))))

(defn- render-sortable-th
  "Render a sortable table header cell.
   Extracted to defn to avoid UIx defui macro limitations.
   visible-from: optional Mantine breakpoint string (e.g. \"sm\") to hide column on mobile."
  [k label sort-fields-set active-field active-dir set-sort! visible-from]
  (let [sortable? (contains? sort-fields-set k)]
    ($ ui/th {:key (str k)
              :visibleFrom visible-from
              :style (when sortable? {:cursor "pointer" :userSelect "none"})
              :onClick (when sortable? #(set-sort! k))}
      ($ ui/group {:gap 4 :wrap "nowrap"}
        label
        (when sortable?
          (sort-indicator k active-field active-dir))))))

;; Segment resolution (default key, key↔param mapping) lives in the pure,
;; node-tested `hooks/list-params` alongside the URL-param logic it shares —
;; `segment-value->param` here was a verbatim copy of `filter-value->param`.
(def ^:private default-segment-key list-params/default-segment-key)
(def ^:private find-segment-key-for-param list-params/find-segment-key-for-param)
(def ^:private find-segment-by-key list-params/find-segment-by-key)

(defn- humanize
  "Convert kebab-case string to Title Case. e.g., \"unconverted-trial\" → \"Unconverted Trial\""
  [s]
  (->> (str/split (str s) #"-")
       (map str/capitalize)
       (str/join " ")))

(defn- hidden-filter-fields
  "Find active filters that have no corresponding UI widget.
   Returns seq of {:field :value} maps for filters in the URL but not
   rendered by any segmented, select, or date-range widget.
   Also includes segmented filter fields where the URL value doesn't
   match any segment (e.g., ?state=trial-booked when segments use groups)."
  [filters filter-config]
  (let [segmented-cfgs (into {} (comp (filter #(= :segmented (:widget %)))
                                      (map (fn [f] [(:field f) f])))
                             filter-config)
        ui-fields (into #{}
                        (mapcat (fn [f]
                                  (if (= :date-range (:widget f))
                                    [(keyword (str (name (:field f)) "-from"))
                                     (keyword (str (name (:field f)) "-to"))]
                                    [(:field f)])))
                        filter-config)
        ;; Also exclude :q (search) — it has its own UI
        known-fields (conj ui-fields :q)]
    (->> filters
         (keep (fn [[field value]]
                 (when value
                   (if-let [seg-cfg (get segmented-cfgs field)]
                     ;; Segmented field: only hidden if URL value doesn't match any segment
                     (when-not (find-segment-key-for-param seg-cfg value)
                       {:field field :value value})
                     ;; Non-segmented: hidden if not a known UI field
                     (when-not (contains? known-fields field)
                       {:field field :value value})))))
         vec)))

(defui select-filter
  "A single select filter dropdown. Extracted as its own component so
   `use-subscribe` is called at the top level (React hooks rule).
   Supports explicit :options or lookup-based options from a remote endpoint."
  [{:keys [agg-key field-config filters set-filter!]}]
  (let [field (:field field-config)
        lookup-options (urf/use-subscribe [::agg.subs/lookup-options agg-key field])
        options (or (not-empty (:options field-config)) lookup-options)]
    ($ ui/select
      {:data (clj->js options)
       :value (get filters field)
       :onChange #(set-filter! field %)
       :placeholder (or (:placeholder field-config) "All")
       :searchable true
       :clearable true
       :size "sm"
       :style {:minWidth "180px"}})))

(defui aggregate-list [{:keys [agg-key resource config query-params]}]
  (let [;; URL sync functions (push = history entry, replace = no history)
        push-qp-fn (push-query-params-fn)
        replace-qp-fn (replace-query-params-fn)
        push-url-fn (when push-qp-fn
                      (fn [params] (push-qp-fn :aggregate/list {:agg resource} params)))
        replace-url-fn (when replace-qp-fn
                         (fn [params] (replace-qp-fn :aggregate/list {:agg resource} params)))

        ;; Active context for auto-filtering (F-041)
        active-context (urf/use-subscribe [::active-context])

        ;; Check for explicit list columns config (F-022)
        list-columns (get-in config [:list :columns])
        ;; Mobile-columns subset for responsive column hiding
        mobile-columns (get-in config [:list :mobile-columns])
        mobile-set (when (seq mobile-columns) (set mobile-columns))
        ;; After-read-fields display config (F-028) — {field-name {:display {:type :badge ...}}}
        after-read-fields (:after-read-fields config)
        ;; Fall back to create schema fields if no list columns defined
        schema (urf/use-subscribe [::agg.subs/schema agg-key :create])
        fields (:fields schema)
        ;; Use list columns if available, otherwise use schema fields
        use-columns? (seq list-columns)
        ;; Column-level link-to: build {col-key (fn [item] url)} for clickable links
        agg-key->resource (urf/use-subscribe [::config.subs/agg-key->resource])
        column-link-fns (when use-columns?
                          (build-column-link-fns list-columns agg-key->resource))
        items (urf/use-subscribe [::agg.subs/all agg-key])
        loading? (urf/use-subscribe [::agg.subs/loading? agg-key])
        pagination (urf/use-subscribe [::agg.subs/pagination agg-key])

        ;; Can create?
        can-create? (and (get-in config [:operations :create])
                         (not (get-in config [:list :hide-create])))
        ;; Panel state (create slide-over)
        panel-open? (urf/use-subscribe [::panel.subs/panel-open?])

        ;; Config-driven filters
        filter-config (get-in config [:list :filters])
        segmented-filters (filterv #(= :segmented (:widget %)) filter-config)
        date-range-filters (filterv #(= :date-range (:widget %)) filter-config)
        select-filters (filterv #(= :select (:widget %)) filter-config)
        ;; Counts for first segmented filter field (nil when no segmented filters)
        counts-field (:field (first segmented-filters))
        counts (urf/use-subscribe [::agg.subs/counts agg-key counts-field])

        ;; Sort config from _config endpoint
        sort-config (get-in config [:list])

        ;; Unified search + filter + sort hook
        {:keys [query set-query! clear-query! filters set-filter! set-range-filter!
                clear-filters! has-active-filters? sort set-sort! page set-page!]}
        (list-hooks/use-list-params {:aggregate agg-key
                                     :filter-config filter-config
                                     :sort-config sort-config
                                     :counts-group-by (get-in config [:operations :counts :group-by] [])
                                     :active-context active-context
                                     :initial-query-params query-params
                                     :push-url-fn push-url-fn
                                     :replace-url-fn replace-url-fn})

        ;; Track active segment key per field (for segmented control value)
        ;; Check URL params first (filters map from hook already has URL values merged),
        ;; fall back to config default
        default-segment-keys (reduce (fn [acc f] (assoc acc (:field f) (default-segment-key f)))
                                     {} segmented-filters)
        initial-segment-keys (reduce (fn [acc f]
                                       (let [field (:field f)
                                             url-val (get filters field)
                                             seg-key (or (when url-val
                                                           (find-segment-key-for-param f url-val))
                                                         (get default-segment-keys field))]
                                         (assoc acc field seg-key)))
                                     {} segmented-filters)
        [segment-keys set-segment-keys!] (uix/use-state initial-segment-keys)

        ;; View matching — override labels when effective filters match a configured view.
        ;; Uses hook filters (URL params merged with defaults) so title matches even when
        ;; a segmented filter default matches the view filter (e.g., period-type=school-year).
        view (match-view agg-key filters)
        effective-label (or (:label view) (:label config))
        effective-label-singular (or (:label-singular view) (:label-singular config))
        ;; Create overrides from matched view (inject values + hide fields)
        create-overrides (:create view)

        ;; Panel handlers (slide-over for create, unless custom submit-endpoint)
        create-op (get-in config [:operations :create])
        submit-endpoint (when (map? create-op) (:submit-endpoint create-op))
        open-create (if submit-endpoint
                      #(navigate! :aggregate/new {:agg resource})
                      #(rf/dispatch [::panel.events/open-panel :create nil]))
        close-panel #(rf/dispatch [::panel.events/close-panel])
        ;; Edit navigates to custom route (if any column has :link-to :route) or default edit page
        detail-route (some (fn [col] (get-in col [:link-to :route])) list-columns)
        open-edit (if detail-route
                    #(navigate! detail-route {:id %})
                    #(navigate! :aggregate/detail {:agg resource :id %}))
        edit-href (if detail-route
                    #(href detail-route {:id %})
                    #(href :aggregate/detail {:agg resource :id %}))
        ;; Mobile filter drawer state
        [filter-drawer-open? set-filter-drawer-open!] (uix/use-state false)
        has-collapsible-filters? (or (seq date-range-filters) (seq select-filters))]

    ;; Fetch schema on mount (list dispatch handled by use-list-params)
    (uix/use-effect
     (fn []
       (rf/dispatch [::agg.events/fetch-schema agg-key :create])
       js/undefined)
     [agg-key])

    ;; Fetch lookup options for select filters on mount
    (uix/use-effect
     (fn []
       (doseq [f filter-config
               :when (and (= :select (:widget f)) (:lookup-url f))]
         (rf/dispatch [::agg.events/lookup agg-key (:lookup-url f) (:field f)]))
       js/undefined)
     [filter-config agg-key])

    ($ :<>
      ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}
        ;; Header row: Title + Add button (desktop inline, mobile full-width below)
      ($ ui/group {:justify "space-between" :mb "md"}
        ($ :div
          ($ ui/title {:order 3} effective-label)
          ($ ui/text {:c "dimmed" :size "sm"}
            (str (or (:total-items pagination) (count items)) " record(s)"))
          ;; A matched view may carry a one-line instruction for the surface
          ;; (e.g. a filtered review list explaining what to do with the rows).
          ;; --text-muted (7:1), not `dimmed` (~3.5:1): instruction text is meant
          ;; to be read. App-wide dimmed contrast is tracked in #865.
          (when-let [view-description (:description view)]
            ($ ui/text {:size "sm" :mt 4 :style {:color "var(--text-muted)"}}
              view-description)))
        (when can-create?
          ($ ui/button
            {:onClick open-create
             :visibleFrom "sm"}
            (str "Add " effective-label-singular))))
      ;; Mobile: full-width create button
      (when can-create?
        ($ ui/button
          {:onClick open-create
           :fullWidth true
           :hiddenFrom "sm"
           :mb "md"}
          (str "Add " effective-label-singular)))

      ;; Segmented control + hidden filter alert
      ;; When custom filters are active (hidden filters detected), hide the segmented
      ;; control and show an info alert with "Clear filter" instead.
      (let [hidden (hidden-filter-fields filters filter-config)]
        (if (seq hidden)
          ($ ui/alert {:variant "light" :color "blue" :mb "md"
                       :icon ($ icons/icon-filter {:size 16})}
            ($ ui/group {:align "center" :gap "xs"}
              ($ ui/text {:size "sm"}
                (str "Filtered by: "
                     (str/join ", " (map #(str (humanize (name (:field %))) " — " (humanize (:value %)))
                                        hidden))))
              ($ ui/button {:variant "subtle" :size "xs" :color "gray"
                            :onClick (fn []
                                       (set-segment-keys! default-segment-keys)
                                       (clear-filters!))}
                "Clear filter")))
          (when (seq segmented-filters)
            ($ seg-bar/segmented-filter-bar
              {:segmented-filters segmented-filters
               ;; Counts currently cover the first segmented field only.
               :counts counts
               :counts-field counts-field
               :segment-keys segment-keys
               :on-select (fn [f field seg-key]
                            (set-segment-keys! (assoc segment-keys field seg-key))
                            (set-filter! field (:value (find-segment-by-key f seg-key))))}))))

      ;; Search + filter row
      (when (or (:searchable? config) has-collapsible-filters? has-active-filters?)
        (let [search-input
              (fn [style]
                (when (:searchable? config)
                  ($ ui/text-input
                    {:placeholder (or (get-in config [:list :search-placeholder])
                                     (str "Search " (str/lower-case effective-label) "..."))
                     :value query
                     :onChange #(set-query! (.. % -target -value))
                     :leftSection ($ icons/icon-search {:size 16})
                     :style (merge {:flex 1} style)})))
              clear-btn
              (when has-active-filters?
                ($ ui/action-icon
                  {:variant "subtle"
                   :color "gray"
                   :onClick (fn []
                              (clear-filters!)
                              (set-segment-keys! default-segment-keys))}
                  ($ icons/icon-filter-off {:size 18})))]
          ($ :<>
            ;; Desktop: all inline
            ($ ui/group {:mb "md" :gap "sm" :wrap "wrap" :visibleFrom "sm"}
              (search-input {:maxWidth 300})
              (for [f date-range-filters]
                (let [field (:field f)
                      from-key (keyword (str (name field) "-from"))
                      to-key (keyword (str (name field) "-to"))]
                  ($ ui/group {:key (name field) :gap "xs" :wrap "nowrap"}
                    ($ ui/date-picker-input
                      {:placeholder "From"
                       :valueFormat "DD MMM YYYY"
                       :clearable true
                       :size "sm"
                       :value (when-let [v (get filters from-key)]
                                (js/Date. (str v "T00:00:00")))
                       :onChange #(set-range-filter! field :from %)
                       :style {:width "160px"}})
                    ($ ui/date-picker-input
                      {:placeholder "To"
                       :valueFormat "DD MMM YYYY"
                       :clearable true
                       :size "sm"
                       :value (when-let [v (get filters to-key)]
                                (js/Date. (str v "T00:00:00")))
                       :onChange #(set-range-filter! field :to %)
                       :style {:width "160px"}}))))
              (for [f select-filters]
                ($ select-filter {:key (name (:field f))
                                  :agg-key agg-key
                                  :field-config f
                                  :filters filters
                                  :set-filter! set-filter!}))
              clear-btn)

            ;; Mobile: search + filter button
            ($ ui/group {:mb "md" :gap "sm" :hiddenFrom "sm"}
              (search-input nil)
              (when has-collapsible-filters?
                ($ ui/button
                  {:variant "light"
                   :size "sm"
                   :leftSection ($ icons/icon-filter {:size 16})
                   :onClick #(set-filter-drawer-open! true)}
                  "Filters"))
              clear-btn)

            ;; Mobile filter drawer
            (when has-collapsible-filters?
              ($ ui/drawer
                {:opened filter-drawer-open?
                 :onClose #(set-filter-drawer-open! false)
                 :position "bottom"
                 :title "Filters"
                 :size "auto"}
                ($ ui/stack {:gap "md" :p "md"}
                  (for [f date-range-filters]
                    (let [field (:field f)
                          from-key (keyword (str (name field) "-from"))
                          to-key (keyword (str (name field) "-to"))]
                      ($ ui/stack {:key (name field) :gap "xs"}
                        ($ ui/text {:size "sm" :fw 500}
                          (or (:label f) (display/humanize-field-name (name field))))
                        ($ ui/group {:gap "xs" :grow true}
                          ($ ui/date-picker-input
                            {:placeholder "From"
                             :valueFormat "DD MMM YYYY"
                             :clearable true :size "sm"
                             :value (when-let [v (get filters from-key)]
                                      (js/Date. (str v "T00:00:00")))
                             :onChange #(set-range-filter! field :from %)
                             :style {:flex 1}})
                          ($ ui/date-picker-input
                            {:placeholder "To"
                             :valueFormat "DD MMM YYYY"
                             :clearable true :size "sm"
                             :value (when-let [v (get filters to-key)]
                                      (js/Date. (str v "T00:00:00")))
                             :onChange #(set-range-filter! field :to %)
                             :style {:flex 1}})))))
                  (for [f select-filters]
                    ($ select-filter {:key (name (:field f))
                                      :agg-key agg-key
                                      :field-config f
                                      :filters filters
                                      :set-filter! set-filter!}))
                  ($ ui/button
                    {:fullWidth true
                     :onClick #(set-filter-drawer-open! false)}
                    "Apply"))))))) ;; end when (search+filter row)

      (cond
        loading?
        ($ ui/centered-loader)

        (empty? items)
        ($ ui/empty-alert
          (list-params/empty-message
           {:filtered? (boolean has-active-filters?)
            :label effective-label
            :configured (get-in config [:list :empty-message])}))

        :else
        (let [sort-fields-set (set (:sort-fields sort-config))
              active-field (ffirst sort)
              active-dir (second (first sort))
              col-visible-from (fn [k] (when (and mobile-set (not (mobile-set k))) "sm"))]
          ($ ui/scroll-area {:type "auto"}
           ($ ui/table {:striped true :highlightOnHover true}
            ($ ui/thead
              ($ ui/tr
                ;; Alert marker column (#742). Its own column rather than a
                ;; dot tucked into the first cell: cells carry `visibleFrom`,
                ;; so the first one is routinely hidden on a phone, and a
                ;; marker that disappears at 375px is worse than none. Narrow
                ;; and unlabelled — the dot's own title carries the meaning.
                ($ ui/th {:key "alert" :style {:width "20px" :padding 0}})
                (if use-columns?
                  (for [col list-columns]
                    (render-sortable-th (:key col) (:label col)
                                        sort-fields-set active-field active-dir set-sort!
                                        (col-visible-from (:key col))))
                  (for [field fields]
                    (render-sortable-th (:name field)
                                        (get-in field [:ui :label]
                                                (display/humanize-field-name (:name field)))
                                        sort-fields-set active-field active-dir set-sort!
                                        nil)))
                ($ ui/th {:key "actions"} "")))
            ($ ui/tbody
              (for [item items]
                ($ ui/tr {:key (:id item)}
                  ;; One declaration, two surfaces: this dot and the headline
                  ;; banner on the record it opens both read [:_meta :alerts]
                  ;; through the same sort, so they cannot name different
                  ;; conditions (F-062).
                  ($ ui/td {:key "alert" :style {:width "20px" :padding 0
                                                 :textAlign "center"}}
                    (let [sev (record.alerts/dot-severity item)]
                      (when sev
                        ($ record.alerts/alert-dot
                          {:severity sev
                           :label (or (some-> (get-in item [:_meta :alerts])
                                              record.alerts/sort-by-severity
                                              first :title)
                                      (str (name sev) " alert"))}))))
                  (if use-columns?
                    (for [col list-columns]
                      (render-column-cell item col after-read-fields column-link-fns query
                                          (col-visible-from (:key col))))
                    (for [field fields]
                      (render-field-cell item field query)))
                  ($ ui/td {:key "actions"}
                    ;; Plain anchor — reitit's document-level click interceptor
                    ;; soft-navigates it (these list rows aren't clickable, so no
                    ;; stopPropagation needed). See SMS-114.
                    ($ ui/anchor {:href (edit-href (:id item))
                                  :c "inherit"}
                      ($ ui/table-action-icon
                        {:color "gray"}
                        ($ icons/icon-edit {:size 18})))))))))))

      ;; Pagination (only when more than 1 page)
      (let [total-pages (:total-pages pagination)]
        (when (and (not loading?) (some? total-pages) (> total-pages 1))
          ($ ui/group {:justify "center" :mt "md"}
            ($ ui/pagination {:total total-pages :value page :onChange set-page!
                              :visibleFrom "sm"})
            ($ ui/pagination {:total total-pages :value page :onChange set-page!
                              :size "sm" :siblings 0 :boundaries 1
                              :hiddenFrom "sm"})))))

      ;; Slide-over panel for create (edit navigates to full page)
      ($ slide-over/slide-over
        {:open? panel-open?
         :on-close close-panel
         :title (str "Create " effective-label-singular)
         :size "lg"}
        (when panel-open?
          ($ panel-form
            {:agg-key agg-key
             :resource resource
             :config config
             :mode :create
             :entity-id nil
             :on-close close-panel
             :create-overrides create-overrides}))))))

;; ============================================================================
;; Generic Create Form
;; ============================================================================

(defui aggregate-create [{:keys [agg-key resource config]}]
  (let [schema (urf/use-subscribe [::agg.subs/schema agg-key :create])
        schema-loading? (urf/use-subscribe [::agg.subs/schema-loading? agg-key])
        [form-data set-form-data!] (uix/use-state {})
        creating? (urf/use-subscribe [::agg.subs/creating? agg-key])
        error (urf/use-subscribe [::agg.subs/error agg-key])

        fields (:fields schema)

        set-field! (fn [k v] (set-form-data! #(assoc % k v)))

        ;; Auto-fill: track which fields are currently auto-filled (readonly)
        [auto-filled-keys set-auto-filled-keys!] (uix/use-state #{})
        handle-auto-fill (uix/use-callback
                          (fn [result]
                            (if result
                              (let [{:keys [values readonly-keys]} result]
                                (set-form-data! #(merge % values))
                                (set-auto-filled-keys! (or readonly-keys #{})))
                              (do (doseq [k auto-filled-keys]
                                    (set-form-data! #(dissoc % k)))
                                  (set-auto-filled-keys! #{}))))
                          [auto-filled-keys])

        handle-submit
        (fn [e]
          (.preventDefault e)
          (rf/dispatch [::agg.events/create agg-key form-data]))]

    ;; Fetch schema on mount
    (uix/use-effect
     (fn []
       (rf/dispatch [::agg.events/fetch-schema agg-key :create])
       js/undefined)
     [agg-key])

    ;; Populate form defaults from schema when it loads
    (uix/use-effect
     (fn []
       (when-let [fields (:fields schema)]
         (let [defaults (->> fields
                             (filter :default)
                             (reduce (fn [m {:keys [name default]}]
                                       (assoc m (keyword name) default))
                                     {}))]
           (when (seq defaults)
             (set-form-data! #(merge defaults %)))))
       js/undefined)
     [schema])

    ($ :<>
      ;; Use grid to match edit view width
      ($ ui/grid {:gutter "md"}
        ($ ui/grid-col {:span #js {:base 12 :md 8}}
          ($ ui/title {:order 3 :mb "xs"} (str "Create " (:label-singular config)))

          ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}

            (when-let [form-error (ff/form-level-error-message error fields)]
              ($ ui/error-alert {:mb "md"} form-error))

            (cond
              schema-loading?
              ($ ui/centered-loader)

              (not schema)
              ($ ui/warning-alert {:title "Loading schema..."}
                "Fetching form configuration...")

              :else
              (let [af-fields (filterv #(get-in % [:ui :auto-fill]) fields)
                    normal-fields (if (seq af-fields)
                                    (remove #(get-in % [:ui :auto-fill]) fields)
                                    fields)
                    render-normal
                    (fn [field]
                      (let [field-key (:name field)]
                        ($ ff/form-field
                          {:key (name field-key)
                           :agg-key agg-key
                           :field field
                           :value (get form-data field-key)
                           :on-change #(set-field! field-key %)
                           :disabled (contains? auto-filled-keys field-key)
                           :form-data form-data})))
                    ;; F-056 create adoption: grouped sections with row layout
                    ;; when declared; flat stack otherwise.
                    create-secs (section-rows/create-sections
                                 (get-in config [:form :sections]) normal-fields)
                    field-area
                    (if (seq create-secs)
                      ($ :<>
                        (for [section create-secs]
                          ($ form.sections/section-fields
                            {:key (name (:id section))
                             :section section
                             :fields normal-fields
                             :agg-key agg-key
                             :form-data form-data
                             :set-field! set-field!
                             :auto-filled-keys auto-filled-keys}))
                        ;; Fields not assigned to any section
                        (for [field (section-rows/unsectioned-fields create-secs normal-fields)]
                          (render-normal field)))
                      (for [field normal-fields]
                        (render-normal field)))]
                ($ :form {:onSubmit handle-submit}
                  ($ ui/stack {:gap "md"}
                    ;; Auto-fill link widgets at top
                    (for [field af-fields]
                      (let [field-key (:name field)]
                        ($ auto-fill/link-widget
                          {:key (name field-key)
                           :field field
                           :value (get form-data field-key)
                           :on-change #(set-field! field-key %)
                           :on-auto-fill handle-auto-fill
                           :disabled false})))

                    field-area

                    ($ ui/group {:justify "flex-end" :mt "md"}
                      ($ ui/button
                        {:variant "subtle" :color "gray"
                         :onClick #(navigate! :aggregate/list {:agg resource})}
                        "Cancel")
                      ($ ui/button
                        {:type "submit"
                         :loading creating?}
                        "Create"))))))))))))

;; ============================================================================
;; Generic Edit Form
;; ============================================================================

(defui child-aggregate-panel
  "Wrapper that resolves child config and renders lib's child-aggregate-list.
   Subscribes to child config so the lib widget stays config-agnostic."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field title read-only?
           child-restrictions custom-add-component parent-record link-to defaults
           refresh-parent exclude-columns junction empty-message add
           initial-edit-id on-slide-over-change exclude-ids]}]
  (let [child-config (urf/use-subscribe [::config.subs/aggregate child-agg-key])
        denied-ops (:denied-operations child-config)
        delete-ui (:delete child-config)
        all-columns (get-in child-config [:list :columns])
        list-columns (if (seq exclude-columns)
                       (let [excluded (set exclude-columns)]
                         (filterv #(not (excluded (:key %))) all-columns))
                       all-columns)
        after-read-fields (:after-read-fields child-config)
        ;; Build view-href-fn from :link-to config (row-level view icon)
        ;; link-to shape: {:aggregate :student :id-field :student-id}
        target-resource (urf/use-subscribe
                         [::config.subs/resource-for-agg-key (:aggregate link-to)])
        view-href-fn (when (and link-to target-resource)
                       (let [id-field (:id-field link-to)]
                         (fn [item]
                           (href :aggregate/detail
                                 {:agg target-resource
                                  :id (get item id-field)}))))
        ;; Build column-level link fns for columns with :link-to
        agg-key->resource (urf/use-subscribe [::config.subs/agg-key->resource])
        column-link-fns (when (seq list-columns)
                          (build-column-link-fns list-columns agg-key->resource))]
    ($ child-agg/child-aggregate-list
      {:parent-agg-key parent-agg-key
       :parent-id parent-id
       :child-agg-key child-agg-key
       :fk-field fk-field
       :title title
       :list-columns list-columns
       :after-read-fields after-read-fields
       :form-field-component ff/form-field
       :read-only? read-only?
       :child-restrictions child-restrictions
       :custom-add-component custom-add-component
       :parent-record parent-record
       :view-href-fn view-href-fn
       :column-link-fns column-link-fns
       :junction junction
       :empty-message empty-message
       :add add
       :defaults defaults
       :refresh-parent refresh-parent
       :initial-edit-id initial-edit-id
       :on-slide-over-change on-slide-over-change
       :exclude-ids exclude-ids
       :denied-ops denied-ops
       :delete-ui delete-ui})))

(defui child-tab [{:keys [agg-key parent-id aggregate title]}]
  (let [items (urf/use-subscribe [::agg.subs/child-items agg-key parent-id aggregate])
        loaded? (urf/use-subscribe [::agg.subs/child-loaded? agg-key parent-id aggregate])
        cnt (count items)]
    ($ ui/tabs-tab {:value (name aggregate)}
      ;; THE BADGE ALWAYS RENDERS ONCE THE COUNT IS KNOWN, dimmed at zero
      ;; (F-060). Hiding it at zero made "none" and "not counted" look
      ;; identical — and empty is information in a B2B tool: "this guardian
      ;; has no invoices" is often exactly what someone came to check.
      ;;
      ;; Before the fetch resolves there is still no badge, because at that
      ;; point we genuinely do not know. Showing a confident `0` that later
      ;; becomes `3` would be a worse lie than showing nothing.
      (if loaded?
        ($ ui/group {:gap 6 :wrap "nowrap"}
          title
          ($ ui/badge {:size "sm"
                       :variant (if (pos? cnt) "light" "outline")
                       :color (when (zero? cnt) "gray")
                       :circle true}
            cnt))
        title))))

(defui aggregate-edit [{:keys [agg-key resource config id query-params tab]}]
  (let [;; Use hooks for form, FSM, and delete — full hook maps kept as
        ;; the :form/:fsm halves of the slot ctx (F-057)
        form-hook (hooks/use-aggregate-form {:aggregate agg-key :mode :edit :id id})
        {:keys [form-data set-field! submit! reset! saving? error dirty? schema schema-loading?]}
        form-hook

        fsm-hook (hooks/use-fsm {:aggregate agg-key :id id})
        {:keys [state state-ui transitions transition! transition-event edit]
         fsm-children :children}
        fsm-hook

        {:keys [delete! deleting? confirm-open? open-confirm! close-confirm!]}
        (hooks/use-aggregate-delete {:aggregate agg-key :id id})

        ;; Get full record for sidebar details
        record (urf/use-subscribe [::agg.subs/current agg-key])
        ;; After-read-fields display config (F-028) for sidebar badges
        after-read-fields (:after-read-fields config)

        ;; Rename for clarity in this component
        delete-modal-open? confirm-open?
        open-delete-modal! open-confirm!
        close-delete-modal! close-confirm!

        ;; Display sections (read-only context above the form)
        detail-display-sections (get-in config [:detail :display-sections])
        ;; Form sections with placement (main vs sidebar) — F-056 :ui :form :sections
        form-sections (get-in config [:form :sections])
        {:keys [sidebar]} (when (seq form-sections)
                            (section-rows/split-sections form-sections))
        ;; Zone 3 facts (F-060). Two declarations feed it, both already
        ;; present: `:ui :detail :sidebar` entries, and the `:readonly`
        ;; sidebar SECTIONS. Reading `:readonly` rather than `:placement` is
        ;; what lets the rail retire without a product-side change — the flag
        ;; is a statement about the FIELDS, not about a layout slot.
        record-facts (->> (into
                           (pinned/facts-from-config
                            {:sidebar-fields (get-in config [:detail :sidebar])
                             :after-read-fields after-read-fields
                             :record record})
                           (pinned/facts-from-sections
                            {:sections (filter
                                        (fn [sec]
                                          (or (not (:visible-states sec))
                                              (contains? (:visible-states sec)
                                                         (:state record))))
                                        sidebar)
                             :field-configs after-read-fields
                             :record record}))
                          (mapv (fn [f]
                                  (cond-> f
                                    (:display-config f)
                                    (assoc :value (display/render-display-value
                                                   (:value f) (:display-config f)))))))
        ;; Rendered at the top of the MAIN column, not across both. Spanning
        ;; the full width pushed the Activity rail down by its own height —
        ;; the rail is a peer of the form, so nothing in the form's column
        ;; should displace it. (The reference has no rail, which is why its
        ;; full-width placement does not translate directly.)
        ;; An alert's `:link` is where the work is — a Messenger thread, a
        ;; tel: or mailto:. An in-app destination routes; anything else is a
        ;; real href and leaves the app, which is the point.
        navigate-to-link!
        (fn [link]
          (when-let [{:keys [href route params query-params]}
                     (record.alerts/link->nav link)]
            (if href
              (set! (.-href js/window.location) href)
              (navigate! route params query-params))))

        ;; Which transition a banner asked for. Data, not a callback: the
        ;; header resolves it against the same transition list its own bar
        ;; reads, so there is one container-resolution path rather than two.
        [launch-request set-launch-request!] (uix/use-state nil)

        record-alerts (get-in record [:_meta :alerts])
        ;; "One condition, one place" (F-059). A condition that fits in a
        ;; single field is already shown by that field, so it TONES the fact
        ;; rather than adding a banner saying the same thing twice — and the
        ;; banner is the one that gives way, because it scrolls off while the
        ;; fact persists. The server marks which is which (#701); this only
        ;; has to read `:fact-field`.
        {fact-alerts true candidate-banners false} (group-by #(some? (:fact-field %))
                                                              record-alerts)
        ;; `:note` never earns a banner — a standing statement on a record
        ;; whose chip already says it would be louder than what it reports.
        ;; It renders as the quiet rail card it always was.
        banner-alerts (filterv record.alerts/banner? candidate-banners)
        note-alerts (filterv (complement record.alerts/banner?) candidate-banners)
        ;; Fold the fact-shaped alerts onto the facts they describe. Tone is
        ;; the affordance: a toned fact is a button that launches the
        ;; transition resolving its condition, through the same header path a
        ;; banner action uses.
        toned-facts
        ;; Keyword BOTH sides. `:fact-field` is a keyword on the server and
        ;; arrives here as the string "follow-up-date" — JSON has no keywords,
        ;; and only the map's KEYS get keywordized on the way in. Matching raw
        ;; fails silently: the banner correctly gives way and nothing tones
        ;; the fact, so the condition disappears entirely.
        (let [by-field (into {} (map (juxt #(some-> (:fact-field %) keyword) identity))
                             fact-alerts)]
          (mapv (fn [{:keys [field] :as f}]
                  (if-let [a (get by-field (some-> field keyword))]
                    (let [{:keys [link]} a
                          action (first (:actions a))]
                      (cond-> (assoc f :tone (:severity a))
                        (:fact-suffix a)
                        (update :value #(str % " · " (:fact-suffix a)))

                        ;; The link renders as its OWN affordance under the
                        ;; value, and is clickable only when it has somewhere
                        ;; to go. A phone number just shows itself.
                        link
                        (assoc :link link
                               :on-link (when (or (:href link) (:route link))
                                          #(navigate-to-link! link)))

                        ;; The pill itself stays a button only when there is a
                        ;; transition and no link competing for the click.
                        (and (not link) action)
                        (assoc :action-label (:label action)
                               :on-action #(set-launch-request!
                                            {:event (:id action) :n (js/Date.now)}))))
                    f))
                record-facts))

        facts-card ($ pinned/pinned-facts {:facts toned-facts})

        ;; F-059 banners. Server-resolved (#701) — the client only sorts, caps
        ;; at two and collapses the rest. Sits ABOVE the facts card and inside
        ;; Details, and scrolls away: once read it should stop taking space,
        ;; because the state chip and the toned fact are what persist.
        alerts-stack (when (seq banner-alerts)
                       ($ record.alerts/alert-stack
                         {:alerts banner-alerts
                          ;; Hand the event to the header rather than acting
                          ;; here: it owns container resolution, so a banner
                          ;; action opens the same drawer or popover the bar
                          ;; button would. The nonce makes a second click on
                          ;; the same action re-fire.
                          :on-action (fn [event-id]
                                       (set-launch-request!
                                        {:event event-id
                                         :n (js/Date.now)}))
                          ;; An alert's :link (#850) — a create-page CTA, a
                          ;; Messenger thread — navigates, unlike :actions which
                          ;; launch a transition through the header.
                          :on-link navigate-to-link!}))
        ;; Build state-display-configs for auto-detecting :state badge rendering
        ;; Maps include/rel key → state-display config from the linked aggregate
        all-agg-configs (urf/use-subscribe [::config.subs/aggregates])
        state-display-configs
        (when (seq detail-display-sections)
          (->> detail-display-sections
               (keep (fn [section]
                       (when-let [link-agg (get-in section [:link :aggregate])]
                         ;; Find the rel-key from field paths (first path segment)
                         (let [rel-keys (->> (:fields section)
                                             (keep (fn [f]
                                                     (let [p (:path f)]
                                                       (when (and (vector? p) (not (vector? (first p)))
                                                                  (> (count p) 1))
                                                         (first p)))))
                                             distinct)
                               sd (get-in all-agg-configs [link-agg :state-display])]
                           (when sd
                             (map (fn [rk] [rk sd]) rel-keys))))))
               (mapcat identity)
               (into {})))
        ;; Link URL builder for display sections with :link config
        agg-resources (urf/use-subscribe [::config.subs/agg-key->resource])
        make-link-url (fn [agg-key id]
                        (when-let [res (get agg-resources agg-key)]
                          (href :aggregate/detail {:agg res :id (str id)})))

        ;; Local state for cancel confirmation
        [cancel-modal-open? set-cancel-modal-open!] (uix/use-state false)

        ;; URL sync for tab state — pushState so browser back navigates between tabs
        push-qp-fn (push-query-params-fn)
        replace-qp-fn (replace-query-params-fn)
        ;; The tab is a PATH SEGMENT, not a query param (F-060):
        ;;   /a/:agg/:id          → Details
        ;;   /a/:agg/:id/:tab     → that tab
        ;; Details is the bare record URL rather than /…/details, so the
        ;; canonical URL of a record is the one people paste. Query params
        ;; that aren't the tab (e.g. ?edit=) ride along untouched.
        update-tab-url! (fn [target-tab]
                          (when push-qp-fn
                            (let [qp (dissoc query-params :tab)]
                              (if (and target-tab (not= target-tab "details"))
                                (push-qp-fn :aggregate/detail-tab
                                            {:agg resource :id id :tab target-tab} qp)
                                (push-qp-fn :aggregate/detail
                                            {:agg resource :id id} qp)))))

        ;; `tab` arrives from :path-params. `?tab=` is still read as a fallback
        ;; so links minted before this change keep working even if they slip
        ;; past the legacy redirect (which only covers /a/:agg/edit/:id).
        url-tab (or tab (:tab query-params) "details")

        ;; Controlled tab state + dirty-check on tab switch
        [active-tab set-active-tab!] (uix/use-state url-tab)
        [pending-tab set-pending-tab!] (uix/use-state nil)

        ;; Sync active-tab from the URL on browser back/forward (popstate).
        ;; Now that the tab is a path segment, Back moves between tabs.
        _ (uix/use-effect
           (fn []
             (when (not= active-tab url-tab)
               (set-active-tab! url-tab))
             js/undefined)
           [url-tab active-tab])

        ;; Guard unsaved edits against leaving the DOCUMENT — refresh, tab
        ;; close, navigating away from the site. One Save for the whole form
        ;; makes the blast radius the entire page (F-049 is the eventual fix).
        ;;
        ;; This does NOT cover in-app browser Back: that is a popstate in the
        ;; same document, so beforeunload never fires. Guarding popstate needs
        ;; sentinel-entry interception, which would also prompt on Back between
        ;; TABS now that tabs are history entries — tracked separately rather
        ;; than bolted on here.
        _ (uix/use-effect
           (fn []
             (if-not dirty?
               js/undefined
               (let [handler (fn [e]
                               ;; Browsers ignore custom text and show their own
                               ;; copy; both lines are required for the prompt
                               ;; to appear at all.
                               (.preventDefault e)
                               (set! (.-returnValue e) "")
                               nil)]
                 (.addEventListener js/window "beforeunload" handler)
                 (fn [] (.removeEventListener js/window "beforeunload" handler)))))
           [dirty?])

        ;; Derived values
        delete-enabled? (get-in config [:operations :delete])
        ;; Match record data against views config to hide irrelevant fields on edit
        ;; (e.g., hide parent-id when editing a school-year)
        edit-view (when record
                    (match-view agg-key (select-keys record
                                          (view-filter-keys agg-key))))
        edit-hide-keys (when-let [create-cfg (:create edit-view)]
                         (into (set (keys (:inject create-cfg)))
                               (:hide create-cfg)))
        all-fields (:fields schema)
        fields (if (seq edit-hide-keys)
                 (remove #(contains? edit-hide-keys (:name %)) all-fields)
                 all-fields)
        loading? (and schema-loading? (nil? form-data))
        has-fsm? (or state-ui (seq transitions))
        embedded-rels (:embedded-relationships config)
        all-children (:children config)
        ;; Split children into tabbed (default) vs inline (displayed below form)
        inline-children (filterv #(= :inline (:layout %)) all-children)
        tabbed-children (filterv #(not= :inline (:layout %)) all-children)
        children tabbed-children
        ;; Read-only when FSM state declares :edit {:readonly true}
        read-only? (:readonly edit)

        ;; Auto-fill: track which fields are currently auto-filled (readonly)
        [auto-filled-keys set-auto-filled-keys!] (uix/use-state #{})
        handle-auto-fill (uix/use-callback
                          (fn [result]
                            (if result
                              (let [{:keys [values readonly-keys]} result]
                                (doseq [[k v] values] (set-field! k v))
                                (set-auto-filled-keys! (or readonly-keys #{})))
                              (do (doseq [k auto-filled-keys] (set-field! k nil))
                                  (set-auto-filled-keys! #{}))))
                          [set-field! auto-filled-keys])

        ;; Cancel handling (needs dirty check for modal)
        handle-cancel (fn []
                        (if dirty?
                          (set-cancel-modal-open! true)
                          (reset!)))

        handle-reset (fn []
                       (reset!)
                       (set-cancel-modal-open! false))

        handle-tab-change
        (fn [target-tab]
          (if (and (= active-tab "details") dirty?)
            (do (set-pending-tab! target-tab))
            (do (set-active-tab! target-tab)
                (update-tab-url! target-tab))))

        handle-discard-and-switch
        (fn []
          (reset!)
          (set-active-tab! pending-tab)
          (update-tab-url! pending-tab)
          (set-pending-tab! nil))

        handle-keep-editing
        (fn []
          (set-pending-tab! nil))]

    (let [;; F-057 detail-page slots — each registered component gets ONE ctx
          ;; map with the data the page already computed; positions are owned
          ;; by the page design. A slot returning nil collapses its band.
          slot-ctx {:aggregate agg-key
                    :record record
                    :schema schema
                    :config config
                    :form form-hook
                    :fsm fsm-hook
                    :refetch! #(rf/dispatch [::agg.events/get agg-key id])}
          page-alerts-slot (when-let [c (detail-slots/slot agg-key :page/alerts)]
                             ($ c slot-ctx))
          main-before-slot (when-let [c (detail-slots/slot agg-key :main/before)]
                             ($ c slot-ctx))
          main-after-slot (when-let [c (detail-slots/slot agg-key :main/after)]
                            ($ c slot-ctx))

          ;; The phone's replacement for the section gutter (#704).
          ;;
          ;; It renders OUTSIDE the form card, not inside sectioned-form-body
          ;; where #704 asked for it, because Mantine's Card sets
          ;; `overflow: hidden` to keep its children inside the border radius.
          ;; That makes the card a clipping container, and a position: sticky
          ;; descendant of a clipping container sticks to THAT box — so the
          ;; rail scrolled away with the form instead of pinning to the top.
          ;;
          ;; Single-source-of-truth survives the move: this is the same
          ;; `form-sections` value sectioned-form-body receives, and
          ;; section-nav does its own :main split with the same
          ;; `split-sections` the gutter uses. Neither side decides for itself
          ;; which sections are navigable.
          ;; Gated on the form being renderable, and NOT merely on config
          ;; having arrived. The rail's observer resolves its targets by id
          ;; when it mounts; mounted a moment early — while the card is still
          ;; showing its loader — it finds no `#form-section-*` elements, and
          ;; its deps (which come from config) never change again, so it stays
          ;; attached to nothing for the life of the page. That is silent: the
          ;; chips render, they just never light up.
          section-nav (when (and (not loading?) schema)
                        ($ form.section-nav/section-nav {:sections form-sections}))

          ;; Props shared with custom detail cards
          detail-props {:agg-key agg-key
                        :id id
                        :config config
                        :form-data form-data
                        :set-field! set-field!
                        :submit! submit!
                        :saving? saving?
                        :error error
                        :schema schema
                        :fields fields
                        :loading? loading?
                        :read-only? read-only?
                        :delete-enabled? delete-enabled?
                        :open-delete-modal! open-delete-modal!
                        :handle-cancel handle-cancel
                        :embedded-rels embedded-rels}

          details-card
          (if-let [custom-component (get-custom-edit-details agg-key)]
            ($ custom-component detail-props)
            ;; Default auto-generated form card
            ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}
              (when-let [form-error (ff/form-level-error-message error fields)]
                ($ ui/error-alert {:mb "md"} form-error))

              (cond
                loading?
                ($ ui/centered-loader)

                (not schema)
                ($ ui/warning-alert {:title "Loading schema..."}
                  "Fetching form configuration...")

                :else
                (let [form-ctx {:agg-key agg-key :form-data form-data
                                :set-field! set-field! :read-only? read-only?
                                :auto-filled-keys auto-filled-keys
                                :handle-auto-fill handle-auto-fill}]
                  ($ :form {:onSubmit submit!}
                    ($ ui/stack {:gap "md"}
                      ;; Form body: sectioned when sections defined, flat otherwise
                      ;; (F-057 primitive, shared with custom pages)
                      ($ form.sections/sectioned-form-body
                        (merge form-ctx {:sections form-sections :fields fields}))

                    ;; Embedded relationships section (F-016)
                    (when (seq embedded-rels)
                      ($ :<>
                        ($ ui/divider {:my "lg"})
                        (for [[rel-name rel-config] embedded-rels]
                          ($ embedded/embedded-list
                            {:key (name rel-name)
                             :agg-key agg-key
                             :parent-id id
                             :rel-name rel-name
                             :rel-config rel-config}))))

                    ;; Buttons at bottom - hidden when read-only
                    (when-not read-only?
                      ($ ui/group {:justify "space-between" :mt "md" :wrap "wrap" :gap "sm"}
                        ;; Left side: Delete button
                        (if delete-enabled?
                          ($ ui/destructive-button
                            {:variant "subtle"
                             :leftSection ($ icons/icon-trash {:size 16})
                             :onClick open-delete-modal!}
                          "Delete")
                        ($ :div))

                      ;; Right side: Cancel and Save
                      ($ ui/group {:gap "sm"}
                        ($ ui/button
                          {:variant "subtle" :color "gray"
                           :onClick handle-cancel}
                          "Cancel")
                        ($ ui/button
                          {:type "submit"
                           :loading saving?}
                          "Save"))))

                    ;; No created/updated here. It was demoted from the rail
                    ;; to a quiet line at this foot, and even that was more
                    ;; prominence than audit metadata earns on a form nobody
                    ;; opened to read it. Parked in #707 until there is a right
                    ;; place for it; `record-meta` stays built for whatever
                    ;; that turns out to be.
                    ))))))

          sidebar-col
          ($ ui/grid-col {:span #js {:base 12 :md 4}
                          :order #js {:base 1 :md 2}}
            ;; `sidebar-sticky-desktop` was never defined in any stylesheet, so
            ;; this sidebar was never sticky (#594). Purpose-specific sticky is
            ;; done per-view where needed (e.g. `.conversation-thread__aside`).
            ($ :<>
              ;; Alerts only now — everything else this rail carried moved to
              ;; the header band or the foot of Details (#699).
              ($ sidebar/edit-sidebar {:notes note-alerts})
              ;; Sidebar form sections (editable fields placed in sidebar)
              ;; :visible-states filters sections by record state
              ;; Readonly sections became pinned facts in the header band, so
              ;; the rail keeps only what is genuinely interactive — Activity,
              ;; Notes, and any editable section a product still places here.
              ;; map-indexed, because the FIRST card in the rail must not
              ;; carry a top margin: it is the top of its column, and 16px of
              ;; unpaired margin left Activity sitting a card-gap lower than
              ;; the main column's first card. It only looked right before
              ;; because a status card sat above it and absorbed the offset.
              (when (seq sidebar)
                (->> sidebar
                     (filter (fn [section]
                               (and (or (:widget section) (not (:readonly section)))
                                    (or (not (:visible-states section))
                                        (contains? (:visible-states section)
                                                   (:state record))))))
                     (map-indexed
                      (fn [idx section]
                  ($ ui/card {:key (name (:id section))
                              :shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md"
                              :withBorder true :mt (when (pos? idx) "md")}
                    (case (:widget section)
                      :notes
                      ($ notes/notes-widget
                        {:agg-key agg-key
                         :record-id id
                         :field-name (:field section)
                         :notes (get record (or (:field section) :notes))
                         :state-display (:state-display config)})
                      :activity
                      ($ activity/activity-widget
                        {:agg-key agg-key
                         :record-id id
                         :collection-field (:collection-field section)})
                      ;; default: editable form fields
                      ($ form.sections/section-fields
                        {:section section
                         :fields fields
                         :agg-key agg-key
                         :form-data form-data
                         :set-field! set-field!
                         :read-only? read-only?})))))))))]

      ;; The tab strip is built here but RENDERED inside the header band —
      ;; Mantine needs the list and the panels under one Tabs context, so the
      ;; band is nested inside the Tabs wrapper below rather than the list
      ;; being lifted out of it.
      (let [tab-strip
            (when (seq children)
              ($ ui/tabs-list {:style {:overflowX "auto" :flexWrap "nowrap"}}
                ($ ui/tabs-tab {:value "details"} "Details")
                (for [child-config children]
                  (let [{:keys [aggregate title fk-value-path]} child-config
                        effective-id (if fk-value-path
                                       (get-in record fk-value-path) id)]
                    ($ child-tab
                      {:key (name aggregate)
                       :agg-key agg-key
                       :parent-id effective-id
                       :aggregate aggregate
                       :title title})))))

            record-header-el
            ;; Record header (F-060 zone 1): identity, state chip, legal
        ;; transitions. Transitions used to live in the right rail, where a
        ;; nine-parameter one had to fit a 320px column and nothing was
        ;; emphasised because every card had equal weight.
        ($ record.header/record-header-with-launcher
          {:title (or (render-display record (:label-template config))
                      (str (:label-singular config)))
           :state state
           :state-ui state-ui
           :transitions transitions
           :on-transition transition!
           :loading-event transition-event
           :agg-key agg-key
           :record record
           ;; Back belongs INSIDE the header band, as it does in the reference
           ;; (`.hdr` holds "← Students"). The shell's generic Back link is
           ;; suppressed for detail routes for the same reason the conversation
           ;; thread suppresses it (#593): a second affordance stacked above a
           ;; view that renders its own is redundant chrome — and here it also
           ;; stranded a link in the strip of page surface above the band.
           :back (when-let [f (back-fn)] {:label "Back" :on-click f})
           :tabs tab-strip
           :launch-request launch-request})]

       ($ :<>
        (if (seq children)
          ;; Tabs wraps the BAND as well as the panels, so the strip can live
          ;; in the band and still share the Tabs context with them.
          ($ ui/tabs {:value active-tab :onChange handle-tab-change}
            record-header-el
            ;; :page/alerts slot — full content width, above both columns
            page-alerts-slot
            ;; Grid inside Tabs so panels + sidebar align naturally
            ($ ui/grid {:gutter "md"}
              ($ ui/grid-col {:span #js {:base 12 :md 8}
                              :order #js {:base 2 :md 1}}
                ($ ui/tabs-panel {:value "details"}
                  alerts-stack
                  facts-card
                  ($ display-sections {:record record :sections detail-display-sections
                                    :make-link-url make-link-url
                                    :state-display-configs state-display-configs})
                  main-before-slot
                  section-nav
                  details-card
                  main-after-slot
                  ;; Inline children render below the form in the details panel.
                  ;; A registered :custom-child-panel replaces the whole section
                  ;; here too (not only in the tab path).
                  (for [child-config inline-children]
                    (let [{:keys [aggregate fk-field title link-to defaults refresh-parent exclude-columns
                                  fk-value-path]
                           child-read-only? :read-only?} child-config
                          effective-id (if fk-value-path (get-in record fk-value-path) id)
                          custom-panel (get-custom-child-panel agg-key aggregate)
                          custom-add (get-custom-child-add agg-key aggregate)]
                      (if custom-panel
                        ($ custom-panel
                          {:key (name aggregate)
                           :parent-agg-key agg-key
                           :parent-id effective-id
                           :child-agg-key aggregate
                           :junction (:junction child-config)
                           :empty-message (:empty-message child-config)
                           :add (:add child-config)
                           :fk-field fk-field
                           :title title
                           :parent-record record})
                        ($ ui/card {:key (name aggregate)
                                    :shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md"
                                    :withBorder true :mt "md"}
                          ($ child-aggregate-panel
                            {:parent-agg-key agg-key
                             :parent-id effective-id
                             :child-agg-key aggregate
                             :junction (:junction child-config)
                             :empty-message (:empty-message child-config)
                             :add (:add child-config)
                             :fk-field fk-field
                             :title title
                             :read-only? (or read-only? child-read-only?)
                             :child-restrictions (get fsm-children aggregate)
                             :custom-add-component custom-add
                             :parent-record record
                             :link-to link-to
                             :defaults defaults
                             :refresh-parent refresh-parent
                             :exclude-columns exclude-columns}))))))
                (for [child-config children]
                  (let [{:keys [aggregate fk-field title link-to defaults refresh-parent exclude-columns
                                fk-value-path]
                         child-read-only? :read-only?} child-config
                        effective-id (if fk-value-path (get-in record fk-value-path) id)
                        custom-panel (get-custom-child-panel agg-key aggregate)
                        custom-add (get-custom-child-add agg-key aggregate)
                        is-active? (= active-tab (name aggregate))
                        ;; The tab is in the PATH now; only :edit stays a query
                        ;; param. Replace, not push — expanding an inline row
                        ;; shouldn't cost a Back press to undo.
                        on-change (when replace-qp-fn
                                    (fn [edit-id]
                                      (let [qp (cond-> {} edit-id (assoc :edit edit-id))]
                                        (replace-qp-fn :aggregate/detail-tab
                                                       {:agg resource :id id :tab (name aggregate)}
                                                       qp))))]
                    ($ ui/tabs-panel
                      {:key (name aggregate) :value (name aggregate)}
                      (if custom-panel
                        ;; Custom panel: replaces entire child tab content
                        ($ custom-panel
                          {:parent-agg-key agg-key
                           :parent-id effective-id
                           :child-agg-key aggregate
                           :junction (:junction child-config)
                             :empty-message (:empty-message child-config)
                             :add (:add child-config)
                           :fk-field fk-field
                           :title title
                           :parent-record record})
                        ;; Standard panel: framework-generated table + slide-over
                        ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}
                          ($ child-aggregate-panel
                            {:parent-agg-key agg-key
                             :parent-id effective-id
                             :child-agg-key aggregate
                             :junction (:junction child-config)
                             :empty-message (:empty-message child-config)
                             :add (:add child-config)
                             :fk-field fk-field
                             :title title
                             :read-only? child-read-only?
                             :child-restrictions (get fsm-children aggregate)
                             :custom-add-component custom-add
                             :parent-record record
                             :link-to link-to
                             :defaults defaults
                             :refresh-parent refresh-parent
                             :exclude-columns exclude-columns
                             :initial-edit-id (when is-active? (:edit query-params))
                             :on-slide-over-change on-change})))))))
              sidebar-col))
          ;; Flat layout: no tabs, so the band stands alone.
          ($ :<>
           record-header-el
           page-alerts-slot
           ($ ui/grid {:gutter "md"}
            ($ ui/grid-col {:span #js {:base 12 :md 8}
                            :order #js {:base 2 :md 1}}
              alerts-stack
              facts-card
              ($ display-sections {:record record :sections detail-display-sections
                                    :make-link-url make-link-url
                                    :state-display-configs state-display-configs})
              main-before-slot
              section-nav
              details-card
              main-after-slot
              ;; Inline children render below the form
              (for [child-config inline-children]
                (let [{:keys [aggregate fk-field title link-to defaults refresh-parent exclude-columns
                              fk-value-path]
                       child-read-only? :read-only?} child-config
                      effective-id (if fk-value-path (get-in record fk-value-path) id)
                      custom-add (get-custom-child-add agg-key aggregate)]
                  ($ ui/card {:key (name aggregate)
                              :shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md"
                              :withBorder true :mt "md"}
                    ($ child-aggregate-panel
                      {:parent-agg-key agg-key
                       :parent-id effective-id
                       :child-agg-key aggregate
                       :junction (:junction child-config)
                             :empty-message (:empty-message child-config)
                             :add (:add child-config)
                       :fk-field fk-field
                       :title title
                       :read-only? child-read-only?
                       :child-restrictions (get fsm-children aggregate)
                       :custom-add-component custom-add
                       :parent-record record
                       :link-to link-to
                       :defaults defaults
                       :refresh-parent refresh-parent
                       :exclude-columns exclude-columns})))))
            sidebar-col)))

      ;; Cancel confirmation modal
      ($ ui/modal
        {:opened cancel-modal-open?
         :onClose #(set-cancel-modal-open! false)
         :title "Discard changes?"
         :centered true}
        ($ ui/stack {:gap "md"}
          ($ ui/text "You have unsaved changes. Are you sure you want to discard them?")
          ($ ui/group {:justify "flex-end" :gap "sm"}
            ($ ui/button
              {:variant "subtle"
               :onClick #(set-cancel-modal-open! false)}
              "Keep editing")
            ($ ui/destructive-button
              {:onClick handle-reset}
              "Discard"))))

      ;; Tab switch dirty-check dialog
      ($ ui/modal
        {:opened (some? pending-tab)
         :onClose handle-keep-editing
         :title "Unsaved changes"
         :centered true}
        ($ ui/stack {:gap "md"}
          ($ ui/text "You have unsaved changes in the Details form. Switching tabs will discard them.")
          ($ ui/group {:justify "flex-end" :gap "sm"}
            ($ ui/button
              {:variant "subtle"
               :onClick handle-keep-editing}
              "Keep editing")
            ($ ui/destructive-button
              {:onClick handle-discard-and-switch}
              "Discard changes"))))

      (when delete-enabled?
        ($ ui/modal
          {:opened delete-modal-open?
           :onClose close-delete-modal!
           :title (str "Delete " (:label-singular config) "?")
           :centered true}
          ($ ui/stack {:gap "md"}
            ($ ui/text
              (str "Are you sure you want to delete this "
                   (str/lower-case (:label-singular config))
                   "? This action cannot be undone."))
            ($ ui/group {:justify "flex-end" :gap "sm"}
              ($ ui/button
                {:variant "subtle" :color "gray"
                 :onClick close-delete-modal!}
                "Cancel")
              ($ ui/destructive-button
                {:loading deleting?
                 :onClick delete!}
                "Delete")))))))))) ;; button, group, stack, modal, when, :<>, header-let, inner-let, outer-let, defui

;; ============================================================================
;; Aggregate Page (routes to list/new/view based on route)
;; ============================================================================

(defui aggregate-page []
  (let [route (urf/use-subscribe (current-route-sub))
        route-name (get-in route [:data :name])
        resource (get-in route [:path-params :agg])  ; URL resource like "persons"
        agg-key (urf/use-subscribe [::config.subs/agg-key-for-resource resource])
        record-id (get-in route [:path-params :id])
        config (urf/use-subscribe [::config.subs/aggregate agg-key])
        ;; Force list remount when navigating back from detail/create/etc.
        ;; Tracks route-name changes and increments a generation counter so
        ;; the list key changes, causing React to create a fresh instance.
        prev-route-ref (uix/use-ref route-name)
        list-gen-ref (uix/use-ref 0)
        _ (let [prev @prev-route-ref]
            (reset! prev-route-ref route-name)
            (when (not= prev route-name)
              (reset! list-gen-ref (inc @list-gen-ref))))]

    (if-not config
      ($ ui/error-alert {:title "Not Found"}
        (str "Unknown aggregate: " resource))

      ;; Key by resource + generation so React unmounts/remounts on navigation
      ;; between aggregates AND when returning to list from detail/create.
      (case route-name
        :aggregate/list ($ aggregate-list {:key (str resource "-" @list-gen-ref) :agg-key agg-key :resource resource
                                          :config config :query-params (get route :query-params)})
        :aggregate/new (let [create-op (get-in config [:operations :create])]
                         (if (and (map? create-op) (:submit-endpoint create-op))
                           ;; Custom create page (injected via configure!)
                           (if-let [custom-page (get-custom-create-page agg-key)]
                             ($ custom-page {:key resource :config config})
                             ($ aggregate-create {:key resource :agg-key agg-key :resource resource :config config}))
                           ;; Standard auto-generated create slide-over
                           ($ aggregate-create {:key resource :agg-key agg-key :resource resource :config config})))
        ;; The detail page IS the editable page (F-060) — one Save, no separate
        ;; edit route. `:aggregate/detail` and `:aggregate/detail-tab` are the
        ;; same page; the tab is a path segment rather than a query param, so
        ;; both land here and the tab is read from :path-params.
        (:aggregate/detail :aggregate/detail-tab)
        ($ aggregate-edit {:key (str resource "-" record-id) :agg-key agg-key
                           :resource resource :config config :id record-id
                           :tab (get-in route [:path-params :tab])
                           :query-params (get route :query-params)})
        ($ ui/text "Unknown route")))))
